<script setup lang="ts">
  import { Button, ButtonAppearance, ButtonSize, Floating, IconName, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { onBeforeUnmount, watch, ref, computed } from 'vue';

  import type { ChildAgeOption, TouristRoom } from '../model/types';
  import RoomCard from './RoomCard.vue';
  import { TOURISTS_TEXTS } from '../config/texts';

  interface TouristsRoomsDropdownProps {
    rooms: TouristRoom[];
    visible: boolean;
    ageOptions: ChildAgeOption[];
    canAddRoom: boolean;
    canRemoveRoom: boolean;
    isVisibleAddButton: boolean;
    isVisibleHeader: boolean;
  }

  interface TouristsRoomsDropdownEmits {
    (e: 'set-adults', payload: { roomIndex: number; value: number }): void;
    (e: 'add-child', payload: { roomIndex: number; age: number }): void;
    (e: 'change-child-age', payload: { roomIndex: number; childIndex: number; age: number }): void;
    (e: 'remove-child', payload: { roomIndex: number; childIndex: number }): void;
    (e: 'add-room'): void;
    (e: 'remove-room', roomIndex: number): void;
    (e: 'close'): void;
  }

  const props = defineProps<TouristsRoomsDropdownProps>();
  const dropdown = ref<HTMLElement | null>(null);
  const isVisibleAges = ref(false);

  const emit = defineEmits<TouristsRoomsDropdownEmits>();


  const isOverflow = computed(() => {
    if(!isVisibleAges.value) return true;
    const height = dropdown.value?.offsetHeight || 0;
    return height <= 370 ? false : true
  });


  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      emit('close');
    }
  };

  watch(
    () => props.visible,
    (visible) => {
      if (visible) {
        document.addEventListener('keydown', onKeydown);
      } else {
        document.removeEventListener('keydown', onKeydown);
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
  });
</script>

<template>
  <Floating :visible="visible" placement="bottom-start">
    <template #reference>
      <slot name="reference" />
    </template>

    <template #floating>
      <div 
        ref="dropdown" 
        :class="['tourists-rooms-dropdown', {
          'tourists-rooms-dropdown_overflow': isOverflow
        }]" 
        data-testid="tourists-rooms-dropdown"
      >
        <div class="tourists-rooms-dropdown__rooms">
          <template v-for="(room, i) in rooms">
            <RoomCard
              :key="`room-${i}`"
              :room="room"
              :index="i"
              :removable="canRemoveRoom"
              :age-options="ageOptions"
              :is-visible-header="isVisibleHeader"
              @visible-ages="isVisibleAges = true"
              @hidden-ages="isVisibleAges = false"
              @update:adults="emit('set-adults', { roomIndex: i, value: $event })"
              @add-child="emit('add-child', { roomIndex: i, age: $event })"
              @change-child-age="emit('change-child-age', { roomIndex: i, ...$event })"
              @remove-child="emit('remove-child', { roomIndex: i, childIndex: $event })"
              @remove-room="emit('remove-room', i)"
            />
            <div
              v-if="i < rooms.length - 1"
              :key="`divider-${i}`"
              class="tourists-rooms-dropdown__divider"
            />
          </template>
        </div>

        <div v-if="isVisibleAddButton" class="tourists-rooms-dropdown__add">
          <Button
            :appearance="ButtonAppearance.Primary"
            :size="ButtonSize.M"
            :disabled="!canAddRoom"
            :icon-left="IconName.Plus"
            class="tourists-rooms-dropdown__button-add"
            data-testid="tourists-rooms-add-room"
            @click="emit('add-room')"
          >
            {{ TOURISTS_TEXTS.addRoom }}
          </Button>

          <div class="tourists-rooms-dropdown__disclaimer" data-testid="tourists-rooms-disclaimer">
            <Typo :variant="TypoVariant.BodySRegular" tag="span"
              >{{ TOURISTS_TEXTS.disclaimerPrefix
              }}<span class="tourists-rooms-dropdown__hl">{{
                TOURISTS_TEXTS.disclaimerHighlight
              }}</span></Typo
            >
          </div>
        </div>
      </div>
    </template>
  </Floating>
</template>

<style scoped lang="scss">

  .tourists-rooms-dropdown {
    background-color: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-s);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl);
    padding: var(--spacing-l) var(--spacing-m);
    width: 339px;
    max-height: 500px;

    &_overflow {
      overflow: auto;
    }

    &__button-add {
      max-width: 178px;
      margin-bottom: var(--spacing-xs);
    }

    &__rooms {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-l);
    }

    &__divider {
      border-top: 1px dashed var(--color-disabled-500);
    }

    &__add {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }

    &__disclaimer {
      color: var(--color-text-primary);
    }

    &__hl {
      background: #fef9c3;
    }
  }
</style>
